import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {useCallback, useState} from 'react'
import {useToastContext} from '@github-ui/toast'
import {ActionList, Box, Button, Dialog} from '@primer/react'
import type {IssueTypesDialogIssueTypes$key} from './__generated__/IssueTypesDialogIssueTypes.graphql'
import type {IssueTypesDialogIssueType$key} from './__generated__/IssueTypesDialogIssueType.graphql'
import {commitUpdateIssueIssueTypeMutation} from '../../mutations/update-issue-issue-type-mutation'
import {LABELS} from '../../constants/labels'
import {ERRORS} from '../../constants/errors'
import {BUTTON_LABELS} from '../..'
import {useAnalytics} from '@github-ui/use-analytics'
import {IssueTypeEntry} from './IssueTypeEntry'

const UNSET_ID = 'UNSET'

const IssueTypeFragment = graphql`
  fragment IssueTypesDialogIssueType on IssueType {
    id
  }
`

export const IssueTypesFragment = graphql`
  fragment IssueTypesDialogIssueTypes on IssueTypeConnection {
    edges {
      node {
        isEnabled
        ...IssueTypeEntry
      }
    }
  }
`

export type IssueTypesDialogProps = {
  onClose: () => void
  isOpen: boolean
  issueId: string
  issueTypes: IssueTypesDialogIssueTypes$key
  initialSelectedType: IssueTypesDialogIssueType$key | null
  returnFocusRef?: React.RefObject<HTMLButtonElement>
}

export const IssueTypesDialog = ({
  onClose,
  issueId,
  issueTypes,
  initialSelectedType,
  isOpen,
  returnFocusRef,
}: IssueTypesDialogProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {sendAnalyticsEvent} = useAnalytics()

  const preloadedIssueTypes = useFragment(IssueTypesFragment, issueTypes)
  const selectedIssueType = useFragment<IssueTypesDialogIssueType$key>(IssueTypeFragment, initialSelectedType)

  const enabledIssueTypes = (preloadedIssueTypes?.edges || []).flatMap(issueType => {
    if (!issueType?.node) return []

    return issueType?.node.isEnabled ? [issueType.node] : []
  })

  const [selectedTypeId, setSelectedTypeId] = useState<string>(selectedIssueType?.id || UNSET_ID)

  const handleSelect = useCallback(() => {
    const issueTypeId = selectedTypeId === UNSET_ID ? null : selectedTypeId
    sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
      initialIssueTypeId: selectedIssueType?.id ?? '',
      issueTypeId: issueTypeId ?? '',
    })

    commitUpdateIssueIssueTypeMutation({
      environment,
      input: {issueId, issueTypeId},
      onError: (e: Error) => {
        onClose()
        // eslint-disable-next-line compat/compat
        reportError(formatError(e.message))
        addToast({type: 'error', message: ERRORS.issueTypeUpdateError})
      },
      onCompleted: onClose,
    })
  }, [selectedTypeId, sendAnalyticsEvent, selectedIssueType?.id, environment, issueId, onClose, addToast])

  return (
    <Dialog
      title={LABELS.issueTypes.selectIssueType}
      onDismiss={onClose}
      isOpen={isOpen}
      sx={{width: '457px'}}
      returnFocusRef={returnFocusRef}
    >
      <Dialog.Header>{LABELS.issueTypes.issueTypeDialogHeader}</Dialog.Header>
      <ActionList
        selectionVariant="single"
        role="menu"
        aria-label="Issue Type selection"
        sx={{maxHeight: '300px', overflowY: 'scroll'}}
      >
        <ActionList.Item
          role="menuitemradio"
          selected={selectedTypeId === UNSET_ID}
          aria-checked={selectedTypeId === UNSET_ID}
          onSelect={() => setSelectedTypeId(UNSET_ID)}
        >
          {LABELS.issueTypes.noIssueTypeOptionName}
        </ActionList.Item>
        {enabledIssueTypes.map((issueType, index) => (
          <IssueTypeEntry
            key={index}
            issueType={issueType}
            selectedTypeId={selectedTypeId}
            setSelectedTypeId={setSelectedTypeId}
          />
        ))}
      </ActionList>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          borderTopColor: 'border.subtle',
          borderTopStyle: 'solid',
          borderTopWidth: '1px',
          py: 2,
          pr: 2,
        }}
      >
        <Button sx={{mr: 2}} onClick={onClose}>
          {BUTTON_LABELS.cancel}
        </Button>
        <Button variant="primary" onClick={handleSelect}>
          {BUTTON_LABELS.changeType}
        </Button>
      </Box>
    </Dialog>
  )
}

const formatError = (message: string) => new Error(ERRORS.issueTypeUpdateErrorReporting(message))

try{ IssueTypesDialog.displayName ||= 'IssueTypesDialog' } catch {}